import {Component, OnDestroy, OnInit, ViewChild} from '@angular/core';
import {ActivatedRoute, NavigationCancel, NavigationEnd, NavigationError, NavigationStart, Router} from '@angular/router';
import {Session} from './domain/session';
import {LoadingDialogComponent} from './component/loading-dialog/loading-dialog.component';
import {Location} from '@angular/common';
import {BreakpointObserver, Breakpoints} from '@angular/cdk/layout';
import {MatSidenav} from '@angular/material/sidenav';
import {CoreConfigurationService} from './core-configuration.service';
import {HttpErrorResponse} from '@angular/common/http';
import {MatDialog, MatDialogRef} from '@angular/material/dialog';
import {environment} from '../environments/environment';
import {SwUpdate} from '@angular/service-worker';
import {Subscription} from 'rxjs/internal/Subscription';
import {timer} from 'rxjs/internal/observable/timer';

@Component({
    selector: 'app-root',
    templateUrl: './app.component.html',
    styleUrls: ['./app.component.css'],
    standalone: false
})
export class AppComponent implements OnInit, OnDestroy {
  searchQuery: string;
  isShowingAppUpdates = false;

  private versionCheckTimerSubscription: Subscription;
  private swUpdateSubscription: Subscription;

  @ViewChild('sidenav') sidenav: MatSidenav;

  loadingDialogRef: MatDialogRef<LoadingDialogComponent>;

  constructor(private location: Location,
              private dialog: MatDialog,
              private route: ActivatedRoute,
              public router: Router,
              public session: Session,
              public breakPointObserver: BreakpointObserver,
              public coreConfiguration: CoreConfigurationService,
              private swUpdate: SwUpdate
  ) {
    breakPointObserver.observe([
      Breakpoints.Handset,
      Breakpoints.TabletPortrait
    ]).subscribe(result => {
      this.coreConfiguration.isMobile = result.matches;
    });
  }

  logout() {
    this.session.reset();
    this.router.navigate(['/']);
  }

  search(query: string) {
    if (this.isLoggedIn()) {
      this.router.navigate(['/admin/shipment', query]);
    } else {
      this.router.navigate(['/track', query]);
    }
  }

  ngOnInit() {
    this.router.events.subscribe((event: any): void => {
      this.navigationInterceptor(event);
      this.coreConfiguration.sidenav = this.sidenav;
    });

    this.updateOnlineStatus();
    this.subscribeUpdateTimer();

    window.addEventListener('online', this.updateOnlineStatus.bind(this));
    window.addEventListener('offline', this.updateOnlineStatus.bind(this));

    if (this.swUpdate.isEnabled) {
      this.swUpdateSubscription = this.swUpdate.versionUpdates.subscribe(event => {
        if (event.type == 'VERSION_READY') {
          console.log(`New version ready: ${event.latestVersion}, current version is: ${event.currentVersion}`);
          this.coreConfiguration.newVersionAvailable = true;
        }
      });
    }
  }

  ngOnDestroy() {
    if (this.versionCheckTimerSubscription != null) {
      this.versionCheckTimerSubscription.unsubscribe();
    }

    if (this.swUpdateSubscription != null) {
      this.swUpdateSubscription.unsubscribe();
    }
  }

  private subscribeUpdateTimer() {
    if (this.versionCheckTimerSubscription == null && this.swUpdate.isEnabled) {
      const updateTimer = timer(2000, environment.versionCheckIntervalInMinutes * 60 * 1000);

      this.versionCheckTimerSubscription = updateTimer.subscribe(v => {
        this.swUpdate.checkForUpdate();
      });
    }
  }

  private updateOnlineStatus() {
    this.coreConfiguration.isOnline = window.navigator.onLine;
  }

  isLoggedIn() {
    return this.session.isValid();
  }

  getVersionString() {
    return environment.version + ' (' + (environment.production ? 'prod' : 'test') + ')';
  }

  showLoading(event) {
    let navigatingToPopup = event.url.includes('popup');
    let navigatingFromPopup = this.route.children.filter(c => c.outlet.includes('popup')).length > 0;

    if (!navigatingToPopup && !navigatingFromPopup) {
      this.coreConfiguration.navigatingInPopupContext = false;

      this.loadingDialogRef = this.dialog.open(LoadingDialogComponent, {
        disableClose: true
      });
    } else {
      this.coreConfiguration.navigatingInPopupContext = true;

      console.log('Navigating ' + (navigatingToPopup ? 'to' : 'from') + ' popup');
    }
  }

  hideLoading() {
    this.dialog.closeAll();
  }

  navigationInterceptor(event): void {
    if (event instanceof NavigationStart) {
      this.showLoading(event);

      if (this.coreConfiguration.isMobile) {
        this.sidenav.close();
      }

      this.coreConfiguration.referringUrl = this.router.url;

      if (event.url === '/' && this.session.isValid()) {
        this.router.navigate(['admin']);
      }

      this.isShowingAppUpdates = event.url.includes('appupdate');

      console.log('Navigating: ' + event.url);
    }
    if (event instanceof NavigationEnd) {
      this.hideLoading();
      this.updateSearchQuery();
      this.updatePageTitle();

      console.log('Navigate finished');
    }
    if (event instanceof NavigationCancel) {
      this.hideLoading();
      console.log('Navigate cancel');
    }
    if (event instanceof NavigationError) {
      this.hideLoading();
      this.handleError(event);

      console.log('Navigate error');
    }
  }

  private updatePageTitle() {
    if (this.route.snapshot.children.length > 0) {
      this.coreConfiguration.showMenu = this.route.snapshot.children[0].data.showMenu;
    }
  }

  private updateSearchQuery() {
    if (this.route.snapshot.children.length > 0 && this.route.snapshot.children[0].paramMap.has('query')) {
      this.searchQuery = this.route.snapshot.children[0].paramMap.get('query');
    } else {
      this.searchQuery = '';
    }
  }

  private handleError(event) {
    if (event.error instanceof HttpErrorResponse) {
      this.handleResponseError(event);
    } else {
      this.handleGenericError(event);
    }
  }

  private handleResponseError(event) {
    console.log(event.error);

    if (event.error.status === 401) {
      this.session.reset();
      this.router.navigate(['/login']);
    } else {
      this.location.go(event.url);

      console.log(event);

      let message = 'Det oppstod en feil under lasting av siden';
      let icon = 'error_outline';
      let css_class = 'error';

      if (event.error.error.errorMessage !== undefined) {
        message = event.error.error.errorMessage;
      }

      if (event.error.error.icon !== undefined) {
        icon = event.error.error.icon;
      }

      if (event.error.error.icon !== undefined) {
        css_class = event.error.error.css_class;
      }

      this.router.navigate(['error', event.error.status, message, icon, css_class], {replaceUrl: true, skipLocationChange: true});
    }
  }

  private handleGenericError(event) {
    this.location.go(event.url);
    this.router.navigate(['error', 'CRASH', event.error.toString(), 'bug_report', 'error'], {replaceUrl: true, skipLocationChange: true});
  }
}
